import {
  Component,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter
} from '@angular/core';

import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { take, takeUntil, Subject, startWith } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomLuxonDateAdapter } from 'src/app/shared/utils/custom-luxon-date-adapter';
import { MY_FORMATS } from 'src/app/shared/utils/date-pickr-custom-luxon-format';
import { DemandeStatusService } from 'src/app/core/services/demande-status.service';
import { DemandeRenouvellementService } from 'src/app/core/services/demande-renouvellement.service';
import { ValidationDetail } from '@models/shared';
import { Renouvellement } from '@models/domains/renewal';
import { AgentsService } from 'src/app/core/services/agents.service';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { CurrentValidationTableComponent } from '../../validation-tables/current-validation-table/current-validation-table.component';
import { ValidationHistoryTableComponent } from '../../validation-tables/validation-history-table/validation-history-table.component';
import type { PdfViewerAction } from '../pdf-viewer.types';
import { FilesAttachmentComponent } from '../../files-attachment/files-attachment.component';
import { FileAttachment } from '@models/shared';
import { FileUtilsService } from 'src/app/core/services/file-utils.service';
import {
  SituationAdministrativeFormValue,
  DestinatairesFormValue,
  PertinenceExistenceFormValue,
  InformationsRenouvellementFormValue,
  CdisationData,
  DemandeRenouvellementFormValue,
  DemandeRenouvellementFormControls
} from '@models/api/requests';
import { SituationAdministrativeFormComponent } from '../../components/situation-administrative-form/situation-administrative-form.component';
import { DestinatairesFormComponent } from '../../components/destinataires-form/destinataires-form.component';
import { BaseUser, BaseDirection } from 'src/app/core/models/shared';
import { PertinenceExistenceFormComponent } from '../../components/pertinence-existence-form/pertinence-existence-form.component';
import { InformationsRenouvellementFormComponent } from '../../components/informations-renouvellement-form/informations-renouvellement-form.component';
import { noWhitespaceValidator } from 'src/app/shared/utils/no-whitespace-validator';

@Component({
    selector: 'app-demande-renouvellement-form',
    imports: [
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatTooltipModule,
    CurrentValidationTableComponent,
    ValidationHistoryTableComponent,
    FilesAttachmentComponent,
    SituationAdministrativeFormComponent,
    DestinatairesFormComponent,
    PertinenceExistenceFormComponent,
    InformationsRenouvellementFormComponent
],
    templateUrl: './demande-renouvellement-form.component.html',
    styleUrls: ['./demande-renouvellement-form.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
        { provide: DateAdapter, useClass: CustomLuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ]
})
export class DemandeRenouvellementFormComponent implements AfterViewInit, OnDestroy {
  private _demandeDetails: Renouvellement | null = null;
  advice: { 
    emetteur: { nom: string; prenom: string };
    destinataire: { nom: string; prenom: string };
    note: string;
  } | null = null;
  @Input() set demandeDetails(value: Renouvellement | null) {
    this._demandeDetails = value;
    this.advice = (value?.destinataireAvis && value?.demandeurAvis)
      ? { 
          emetteur: { nom: value.demandeurAvis.nom ?? '', prenom: value.demandeurAvis.prenom ?? '' },
          destinataire: { nom: value.destinataireAvis.nom ?? '', prenom: value.destinataireAvis.prenom ?? '' },
          note: value.demandeAvisMotif ?? ''
        }
      : null;
    if (value) {
      this.populateFormsWithDetails();
      this.loadValidationSteps();
      this.loadPj();
    }
  }
  get demandeDetails(): Renouvellement | null {
    return this._demandeDetails;
  }

  @Input() set motifRefus(value: string) {
    this.motifRefusControl.setValue(value);
  }

  private _readOnly: boolean = true;
  @Input() set readOnly(value: boolean) {
    this._readOnly = value;
    this.PJreadOnly = value;
    if (this.renouvellementForm) {
      this.updateFormsDisabledState(value);
    }
  }
  get readOnly(): boolean {
    return this._readOnly;
  }

  @Input() viewMode: PdfViewerAction = 'view';

  @ViewChildren(MatExpansionPanel) allpanels!: QueryList<MatExpansionPanel>;

  // Main form group
  renouvellementForm: FormGroup<DemandeRenouvellementFormControls>;
  motifRefusControl: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [noWhitespaceValidator]
  });

  // Cdisation data for the informations renouvellement form
  cdisationData: CdisationData = {
    dateFinContrat: '',
    nbJoursAvantCdisation: 0,
    nbJoursAbsences: 0,
    nbJoursAbsencesSNF: 0,
    absenceMO: 0,
    absenceATMP: 0,
    absenceMDML: 0,
    absenceMAPA: 0,
    totalJoursAbsences: 0,
    flagCdisation: false,
    absenceDateDebut: '',
    absenceDateFin: '',
    snfDateDebut: '',
    snfDateFin: ''
  };

  private readonly destroy$ = new Subject<void>();
  validationSteps: ValidationDetail[] = [];

  attachments: FileAttachment[] = [];
  showAttachments = false;

  // pièces jointes
  @ViewChild('fileInput') fileInput!: HTMLInputElement;
  @Output() filesSelected = new EventEmitter<File[]>();
  selectedFileNames: File[] = [];
  invalidFile: boolean = false;
  isDragging = false;
  PJreadOnly: boolean = false;
  PJTotal: number;
  PJObligatoire: boolean = true;
  selectedDirection: BaseDirection | null = null;
  selectedService: SituationAdministrativeFormValue['serviceFonctionnel'] | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly agentsService: AgentsService,
    private readonly referentielsService: ReferentielsService,
    private readonly demandeRenouvellementService: DemandeRenouvellementService,
    public readonly demandeStatusService: DemandeStatusService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fileUtils: FileUtilsService
  ) {
    this.initializeForms();
  }

  ngAfterViewInit(): void {
    // Open the first panel by default
    if (this.allpanels?.first) {
      this.allpanels.first.open();
    }
  }

  initializeForms(): void {
    this.renouvellementForm = this.fb.group<DemandeRenouvellementFormControls>({
      situationAdministrativeData: this.fb.control<SituationAdministrativeFormValue | null>(null, Validators.required),
      informationsRenouvellementData: this.fb.control<InformationsRenouvellementFormValue | null>(null, Validators.required),
      pertinenceExistenceData: this.fb.control<PertinenceExistenceFormValue | null>(null, Validators.required),
      destinatairesData: this.fb.control<DestinatairesFormValue | null>(null, Validators.required),
      attachmentsData: this.fb.control<number | null>(null, Validators.required)
    });

    // Update disabled state based on readOnly
    this.updateFormsDisabledState(this.readOnly);

    this.renouvellementForm
      .get('situationAdministrativeData')
      ?.valueChanges.pipe(startWith(this.renouvellementForm.get('situationAdministrativeData')?.value), takeUntil(this.destroy$))
      .subscribe((value) => {
        const informations = value ?? null;
        this.updateDestinatairesStructure(informations as SituationAdministrativeFormValue | null);
      });
  }

  updateFormsDisabledState(disabled: boolean): void {
    if (disabled) {
      this.renouvellementForm.disable({ emitEvent: false });
    } else {
      this.renouvellementForm.enable({ emitEvent: false });
    }
  }

  // Form population
  populateFormsWithDetails(): void {
    if (!this.demandeDetails) return;

    // Reset the form first to ensure clean state
    this.renouvellementForm.reset();

    // Populate situation administrative form
    if (this.demandeDetails) {
      // Load category data
      this.referentielsService
        .getCategoriePoste()
        .pipe(take(1))
        .subscribe({
          next: (categories) => {
            if (this.demandeDetails) {
              // Find the category by ID
              const categoriePoste = categories.find((c: any) => c.id === this.demandeDetails?.agent?.categoriePoste) || null;

              // Prepare situation administrative form value
              const situationAdministrativeValue: SituationAdministrativeFormValue = {
                agent: this.demandeDetails.agent,
                identifiantAgent: this.demandeDetails.agent?.identifiant,
                categoriePoste: categoriePoste,
                grade: this.demandeDetails.agent?.grade,
                dateEntree: this.demandeDetails.dateEntree,
                posteOccupe: this.demandeDetails.posteOccupe,
                tauxEmploi: this.demandeDetails.tauxEmploi,
                fonction: this.demandeDetails.agent?.fonction,
                pole: this.demandeDetails.pole,
                direction: this.demandeDetails.direction,
                serviceFonctionnel: this.demandeDetails.serviceFonctionnel,
                typeContrat: this.demandeDetails.typeContrat,
                typeRenouvellement: this.demandeDetails.typeRenouvellement,
                agentRemplace: this.demandeDetails.agentRemplace,
                dateRange: {
                  start: this.demandeDetails.dateDebutContrat,
                  end: this.demandeDetails.dateFinContrat
                }
              };

              // Set the form value
              this.renouvellementForm.get('situationAdministrativeData')?.setValue(situationAdministrativeValue);
              this.updateDestinatairesStructure(situationAdministrativeValue);
            }
          },
          error: (error) => {
            console.error('Error loading categories:', error);
          }
        });
    }

    // Populate informations renouvellement form
    // Determine which motif is selected based on boolean flags
    let selectedMotif = null;
    if (this.demandeDetails.contratMemePoste) selectedMotif = 'contratMemePoste';
    else if (this.demandeDetails.contratConditionsDiffs) selectedMotif = 'contratConditionsDiffs';
    else if (this.demandeDetails.refusContratDemande) selectedMotif = 'refusContratDemande';
    else if (this.demandeDetails.refusContratConditions) selectedMotif = 'refusContratConditions';

    const informationsRenouvellementValue: InformationsRenouvellementFormValue = {
      motif: selectedMotif,
      confirmRefus: selectedMotif === 'refusContratDemande' // Set to true if refus is selected
    };

    // Set cdisation data if available
    if (this.demandeDetails) {
      // Set some default values based on available data
      this.cdisationData = {
        ...this.cdisationData,
        dateFinContrat: this.demandeDetails.dateFinContrat || '',
        flagCdisation: false // Default value, should be determined by business logic
      };
    }

    this.renouvellementForm.get('informationsRenouvellementData')?.setValue(informationsRenouvellementValue);

    // Populate pertinence existence form
    const pertinenceExistenceValue: PertinenceExistenceFormValue = {
      pertinenceMission: this.demandeDetails.pertinenceMission,
      renouvellementAJustifier: this.demandeDetails.renouvellementJustifie
    };
    this.renouvellementForm.get('pertinenceExistenceData')?.setValue(pertinenceExistenceValue);

    // Populate destinataires form with empty array for destinataires if not present
    // Using structural typing - TypeScript will accept compatible interface structures
    const destinatairesValue: DestinatairesFormValue = {
      destinataires: [],
      destinataireValidation: this.demandeDetails.destinataireValidation as BaseUser | null,
      destinataireChargeGpec: this.demandeDetails.destinataireCharge as BaseUser | null,
      destinataireDrh: this.demandeDetails.destinataireDRH as BaseUser | null,
      destinataireDg: this.demandeDetails.destinataireDG as BaseUser | null,
      destinataireDgs: this.demandeDetails.destinataireDgs as BaseUser | null,
      destinataireResponsable: this.demandeDetails.destinataireResponsable as BaseUser | null,
      destinataireArchivageRh: this.demandeDetails.destinataireArchivage as BaseUser | null,
      directionDemande: this.demandeDetails.direction as BaseDirection | null
    };

    this.renouvellementForm.get('destinatairesData')?.setValue(destinatairesValue);

    // Update form disabled state
    this.updateFormsDisabledState(this.readOnly);
  }

  getSafeStatusText(): string {
    if (this.demandeDetails?.statut && typeof this.demandeDetails.statut === 'string') {
      return this.demandeStatusService.getStatusText(this.demandeDetails.statut);
    }
    return 'Non renseigné';
  }

  getSafeStatusClass(): string {
    if (this.demandeDetails?.statut && typeof this.demandeDetails.statut === 'string') {
      return this.demandeStatusService.getStatusClass(this.demandeDetails.statut);
    }
    return 'status-pending';
  }

  // Type-safe form value access methods
  getFormValue(): DemandeRenouvellementFormValue | null {
    return this.renouvellementForm.valid ? this.renouvellementForm.value as DemandeRenouvellementFormValue : null;
  }

  getRawFormValue(): DemandeRenouvellementFormValue {
    return this.renouvellementForm.getRawValue();
  }

  isFormValid(): boolean {
    return this.renouvellementForm.valid;
  }

  getFormErrors(): { [key: string]: any } | null {
    return this.renouvellementForm.errors;
  }

  markAllFieldsAsTouched(): void {
    this.renouvellementForm.markAllAsTouched();
  }

  loadValidationSteps(): void {
    if (this._demandeDetails?.id) {
      this.demandeRenouvellementService
        .getDetailsValidations(this._demandeDetails.id, 'Renouvellement')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (steps: ValidationDetail[]) => {
            
            this.validationSteps = steps;
            console.log('validation',this.validationSteps);
          },
          error: (error: Error) => {
            console.error('Error loading validation steps:', error);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDestinatairesStructure(informations: SituationAdministrativeFormValue | null): void {
    this.selectedDirection = informations?.direction ?? null;
    this.selectedService = informations?.serviceFonctionnel ?? null;
  }

  // Update cdisation data when an agent is selected
  updateCdisationData(agent: any): void {
    if (agent) {
      this.cdisationData = {
        dateFinContrat: agent.cdisationDate || '',
        nbJoursAvantCdisation: agent.cdisationJoursRestants || 0,
        nbJoursAbsences: agent.absenceTotal || 0,
        nbJoursAbsencesSNF: agent.snfNb || 0,
        absenceMO: agent.absenceMoNb || 0,
        absenceATMP: agent.absenceAtmpNb || 0,
        absenceMDML: agent.absenceMdmlNb || 0,
        absenceMAPA: agent.absenceMapaNb || 0,
        totalJoursAbsences: agent.absenceTotal || 0,
        flagCdisation: agent.flagCdisation || false,
        absenceDateDebut: agent.absenceDateDebut || '',
        absenceDateFin: agent.absenceDateFin || '',
        snfDateDebut: agent.snfDateDebut || '',
        snfDateFin: agent.snfDateFin || ''
      };
    }
  }

  // pièces jointes ----------------------------------------------
  loadPj(): void {
    if (this.demandeDetails?.id) {
      this.demandeRenouvellementService.getPj(this.demandeDetails.id).subscribe({
        next: (files: FileAttachment[]) => {
          this.attachments = files.map((pj: FileAttachment) => ({
            id: pj.id,
            originalFileName: pj.originalFileName,
            fileName: pj.fileName,
            blobattachment: this.fileUtils.base64ToBlob(pj.content),
            content: pj.content,
            demande: pj.demande
          }));
          this.showAttachments = files.length > 0;
          this.PJTotal = files.length;
          this.cdr.markForCheck();

          this.verifPJObligatoire(this.PJObligatoire);
        },
        error: (error: unknown) => {
          this.renouvellementForm.get('attachmentsData')?.setValidators(null);
          this.renouvellementForm.get('attachmentsData')?.updateValueAndValidity();
          console.error('Error loading attachments:', error);
        }
      });
    }
  }

  closeAttachments(): void {
    this.showAttachments = false;
  }

  removePJ(event: FileAttachment[]): void {
    if (this.readOnly) {
      return;
    }
    // reçoit la pièce jointe à supprimer
    const fichierASuppr = event[0];
    const idPj = fichierASuppr.id;

    // demande la suppression de la PJ
    if (this._demandeDetails?.id) {
      this.demandeRenouvellementService
        .deletePj(this._demandeDetails.id, idPj?.toString() ?? '')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.PJTotal -= 1;
            // console.error('suppression de pièce jointe avec succès');
          },
          error: (error) => {
            console.error('Erreur de suppression de pièce jointe:', error);
          }
        });
    }
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileSelected(event: Event | FileList): void {
    const inputEl = (event as Event & { target: HTMLInputElement })?.target;
    const fileSelected = inputEl?.files || event as FileList;

    try {
      if (fileSelected) {
        const files = Array.from(fileSelected) as File[];
        const pdfFiles = files.filter((file) => file.type === 'application/pdf');

        if (pdfFiles.length === files.length) {
          this.invalidFile = false;
          this.selectedFileNames = [...this.selectedFileNames, ...pdfFiles];

          if (this._demandeDetails?.id) {
            this.demandeRenouvellementService
              .postPj(this._demandeDetails.id, this.selectedFileNames)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (result) => {
                  this.loadPj();
                },
                error: (error) => {
                  console.error("Erreur de l'ajout de pièce jointe:", error);
                }
              });
          }
        } else {
          this.invalidFile = true;
        }
      }
    } finally {
      if (inputEl) {
        // Reset input so selecting the same file again triggers a change event.
        inputEl.value = '';
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  handleDragDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      const droppedFiles = Array.from(event.dataTransfer.files) as File[];
      const pdfFiles = droppedFiles.filter((file) => file.type === 'application/pdf');

      if (pdfFiles.length) {
        this.invalidFile = false;

        const fileList = this.createFileList(pdfFiles);
        this.onFileSelected(fileList);
      } else {
        this.invalidFile = false;
      }
    }
  }

  // rend les pièces jointes obligatoire suivant les options "Informations sur le renouvellement"
  verifPJObligatoire(event: boolean): void {
    this.PJObligatoire = event;

    if (this.PJObligatoire && this.PJTotal == 0) {
      this.renouvellementForm.get('attachmentsData')?.setValue(null);
      this.renouvellementForm.get('attachmentsData')?.setValidators(Validators.required);
    } else {
      this.renouvellementForm.get('attachmentsData')?.setValue(this.PJTotal);
      this.renouvellementForm.get('attachmentsData')?.setValidators(null);
    }

    this.renouvellementForm.get('attachmentsData')?.updateValueAndValidity();
  }

  private createFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    return dataTransfer.files;
  }
  // ------------------------------------------------
}
